/**
 * SidebarStep4 - Payroll & Additional Inputs (Historical BI Path)
 * 
 * This component provides payroll and additional expense inputs for the Historical BI calculation.
 * All sections visible on one scrollable page matching the wireframe.
 * 
 * Preserves ALL functionality from Step4_AdditionalInputs.tsx - only layout changes.
 * 
 * BLS DATA TRANSPARENCY:
 * - Shows clear "BLS Average" badges when industry defaults are used
 * - Displays warning banner when BLS data is present
 * - Tracks source for each expense field (verified, estimate, bls_average)
 * - Encourages users to replace with actual figures for defensibility
 */

import React, { useState, useEffect, useCallback } from 'react';
import { 
  DollarSign, 
  Info, 
  CheckCircle,
  Wallet,
  Receipt,
  AlertTriangle,
  TrendingUp,
  Sparkles,
  FileText,
  Edit3
} from 'lucide-react';
import WizardV2Layout from './WizardV2Layout';
import { formatCurrency } from '../../../utils/formatCurrency';

// BLS Industry Templates for Fixed Expenses (from original)
const INDUSTRY_TEMPLATES: Record<string, {
  officer_salary: number;
  rent: number;
  utilities: number;
  insurance: number;
  other_fixed: number;
}> = {
  restaurant: { officer_salary: 6500, rent: 8000, utilities: 1200, insurance: 800, other_fixed: 1500 },
  retail: { officer_salary: 5500, rent: 6000, utilities: 800, insurance: 600, other_fixed: 1000 },
  professional_services: { officer_salary: 8000, rent: 4000, utilities: 500, insurance: 500, other_fixed: 800 },
  manufacturing: { officer_salary: 7500, rent: 10000, utilities: 2500, insurance: 1200, other_fixed: 2000 },
  healthcare: { officer_salary: 9000, rent: 7000, utilities: 1000, insurance: 1500, other_fixed: 1200 },
  construction: { officer_salary: 7000, rent: 3000, utilities: 600, insurance: 2000, other_fixed: 1500 },
  hospitality: { officer_salary: 6000, rent: 9000, utilities: 1500, insurance: 900, other_fixed: 1800 },
  default: { officer_salary: 6000, rent: 5000, utilities: 800, insurance: 700, other_fixed: 1000 }
};

interface SidebarStep4Props {
  goToStep?: (step: number) => void;
  onNext: (data: any) => void;
  onBack: () => void;
  data: any;
  onDataUpdate?: (data: any) => void;
  onSave?: (explicitData?: any) => Promise<void>;
  isSaving?: boolean;
  lastSaved?: Date | null;
  highestCompletedStep?: number;
  claimId?: string;
}

export default function SidebarStep4({ 
  onNext, 
  onBack, 
  data, 
  onDataUpdate, 
  onSave,
  isSaving,
  lastSaved,
  goToStep,
  highestCompletedStep,
  claimId
}: SidebarStep4Props) {
  // Form state - ALL fields from original Step4_AdditionalInputs
  const [formData, setFormData] = useState({
    // Payroll Information
    pre_incident_payroll: data.pre_incident_payroll || '',
    coverage_period_payroll: data.coverage_period_payroll || '',
    
    // Payroll source tracking (auto-detected vs manual)
    payroll_sources: data.payroll_sources || {
      pre_incident_payroll: 'manual' as const,
      coverage_period_payroll: 'manual' as const
    },
    
    // Extra Expenses Breakdown (from wireframe)
    extra_expense_breakdown: data.extra_expense_breakdown || {
      temporary_relocation: 0,
      temporary_equipment: 0,
      expedited_services: 0,
      marketing_recovery: 0,
      other_extra: 0
    },
    
    // Extra Expense source tracking (auto-detected vs manual)
    extra_expense_sources: data.extra_expense_sources || {
      temporary_relocation: 'manual' as const,
      temporary_equipment: 'manual' as const,
      expedited_services: 'manual' as const,
      marketing_recovery: 'manual' as const,
      other_extra: 'manual' as const
    },
    
    // Fixed Expenses (Monthly) with source tracking
    fixed_expenses: data.fixed_expenses || {
      officer_salary: 0,
      rent: 0,
      utilities: 0,
      insurance: 0,
      other_fixed: 0
    },
    fixed_expense_sources: data.fixed_expense_sources || {
      officer_salary: 'bls_average' as const,
      rent: 'bls_average' as const,
      utilities: 'bls_average' as const,
      insurance: 'bls_average' as const,
      other_fixed: 'bls_average' as const
    },
    
    // Policy threshold
    threshold: data.threshold || 0,
    threshold_type: data.threshold_type || 'dollar',
    
    // Legacy fields
    accountant_fees: data.accountant_fees || 0,
    continuing_expenses: data.continuing_expenses || 0,
    extra_expenses: data.extra_expenses || 0,
    adjustment_notes: data.adjustment_notes || ''
  });

  const [showFixedExpenses, setShowFixedExpenses] = useState(true);
  const [templateApplied, setTemplateApplied] = useState(false);
  const [loadingDefaults, setLoadingDefaults] = useState(false);

  // Auto-calculate payroll and fixed expenses from available data sources
  useEffect(() => {
    // SOURCE 1: Try canonical_data periods (most reliable - from bank statements)
    const canonicalPeriods = data.unified_upload_data?.canonical_data?.periods || [];
    
    if (canonicalPeriods.length > 0 && (!formData.pre_incident_payroll || formData.pre_incident_payroll === '')) {
      // Group periods by year
      const periodsByYear: Record<number, any[]> = {};
      canonicalPeriods.forEach((p: any) => {
        const year = p.year;
        if (!periodsByYear[year]) periodsByYear[year] = [];
        periodsByYear[year].push(p);
      });
      
      const years = Object.keys(periodsByYear).map(Number).sort();
      
      if (years.length >= 1) {
        // Pre-incident payroll: use second-to-last year if available, otherwise last year
        const preIncidentYear = years.length >= 2 ? years[years.length - 2] : years[0];
        const preIncidentPeriods = periodsByYear[preIncidentYear] || [];
        const preIncidentPayroll = preIncidentPeriods.reduce((sum: number, p: any) => 
          sum + (p.employee_wages || 0), 0);
        
        // Coverage period payroll: use last year
        const coverageYear = years[years.length - 1];
        const coveragePeriods = periodsByYear[coverageYear] || [];
        const coveragePayroll = coveragePeriods.reduce((sum: number, p: any) => 
          sum + (p.employee_wages || 0), 0);
        
        // Fixed costs: calculate monthly averages from data
        const allPeriods = canonicalPeriods;
        const avgRent = allPeriods.reduce((sum: number, p: any) => sum + (p.fixed_costs || 0), 0) / allPeriods.length;
        
        // Update form with calculated values
        const newFormData: any = { ...formData };
        let updated = false;
        
        if (preIncidentPayroll > 0 && !formData.pre_incident_payroll) {
          newFormData.pre_incident_payroll = Math.round(preIncidentPayroll).toString();
          newFormData.payroll_sources = {
            ...formData.payroll_sources,
            pre_incident_payroll: 'bank_data'
          };
          updated = true;
          console.log(`📊 Auto-populated pre_incident_payroll from ${preIncidentPeriods.length} months of bank data: $${Math.round(preIncidentPayroll).toLocaleString()}`);
        }
        if (coveragePayroll > 0 && !formData.coverage_period_payroll) {
          newFormData.coverage_period_payroll = Math.round(coveragePayroll).toString();
          newFormData.payroll_sources = {
            ...newFormData.payroll_sources,
            coverage_period_payroll: 'bank_data'
          };
          updated = true;
          console.log(`📊 Auto-populated coverage_period_payroll from ${coveragePeriods.length} months of bank data: $${Math.round(coveragePayroll).toLocaleString()}`);
        }
        
        // Auto-populate fixed expenses from actual data if we have it
        if (!templateApplied && avgRent > 0) {
          // Analyze transactions to get more precise fixed cost breakdowns
          let rentTotal = 0;
          let utilitiesTotal = 0;
          let insuranceTotal = 0;
          let otherFixedTotal = 0;
          let monthCount = 0;
          
          // Look at transactions to categorize fixed costs
          canonicalPeriods.forEach((p: any) => {
            monthCount++;
            const transactions = p.transactions || [];
            
            transactions.forEach((txn: any) => {
              const category = (txn.category || '').toUpperCase();
              const description = (txn.description || '').toUpperCase();
              const amount = Math.abs(txn.amount || 0);
              
              // Categorize by transaction category/description
              if (category.includes('RENT') || category.includes('OCCUPANCY') || 
                  description.includes('RENT') || description.includes('LANDLORD') || description.includes('LEASE')) {
                rentTotal += amount;
              } else if (category.includes('UTILITIES') || 
                         description.includes('ELECTRIC') || description.includes('GAS') || 
                         description.includes('WATER') || description.includes('UTILITY')) {
                utilitiesTotal += amount;
              } else if (category.includes('INSURANCE') || description.includes('INSURANCE')) {
                insuranceTotal += amount;
              } else if (category.includes('FIXED') || category.includes('BANK') || 
                         category.includes('INTEREST') || category.includes('DEBT')) {
                otherFixedTotal += amount;
              }
            });
          });
          
          const hasDetailedData = rentTotal > 0 || utilitiesTotal > 0 || insuranceTotal > 0;
          
          if (hasDetailedData && monthCount > 0 && formData.fixed_expenses.rent === 0) {
            // Calculate monthly averages from actual transaction data
            newFormData.fixed_expenses = {
              ...formData.fixed_expenses,
              rent: Math.round(rentTotal / monthCount),
              utilities: Math.round(utilitiesTotal / monthCount),
              insurance: Math.round(insuranceTotal / monthCount),
              other_fixed: Math.round(otherFixedTotal / monthCount),
            };
            newFormData.fixed_expense_sources = {
              ...formData.fixed_expense_sources,
              rent: rentTotal > 0 ? 'verified' : 'bls_average',
              utilities: utilitiesTotal > 0 ? 'verified' : 'bls_average', 
              insurance: insuranceTotal > 0 ? 'verified' : 'bls_average',
              other_fixed: otherFixedTotal > 0 ? 'verified' : 'bls_average',
            };
            updated = true;
            console.log(`📊 Auto-populated fixed expenses from ${monthCount} months of transaction data`);
          } else if (!hasDetailedData && formData.fixed_expenses.rent === 0) {
            // Fall back to estimating from total fixed_costs
            const monthlyFixed = avgRent;
            newFormData.fixed_expenses = {
              ...formData.fixed_expenses,
              rent: Math.round(monthlyFixed * 0.5), // Estimate rent as 50% of fixed
              utilities: Math.round(monthlyFixed * 0.15),
              insurance: Math.round(monthlyFixed * 0.1),
              other_fixed: Math.round(monthlyFixed * 0.25),
            };
            newFormData.fixed_expense_sources = {
              ...formData.fixed_expense_sources,
              rent: 'estimate',
              utilities: 'estimate', 
              insurance: 'estimate',
              other_fixed: 'estimate',
            };
            updated = true;
          }
        }
        
        if (updated) {
          setFormData(newFormData);
        }
      }
      return; // Don't also process financial_data
    }
    
    // SOURCE 2: Fall back to financial_data format (legacy)
    if (data.financial_data && !formData.pre_incident_payroll) {
      const years = data.calculated_years || [];
      if (years.length >= 2) {
        const preIncidentYear = years[years.length - 2];
        const yearKey = `data_${preIncidentYear}`;
        const yearData = data.financial_data[yearKey];
        
        if (Array.isArray(yearData)) {
          const annualPayroll = yearData.reduce((sum: number, month: any) => {
            return sum + (month.payroll || month.employee_wages || 0);
          }, 0);
          if (annualPayroll > 0) {
            setFormData(prev => ({
              ...prev,
              pre_incident_payroll: Math.round(annualPayroll).toString()
            }));
          }
        }
      }
      
      if (years.length >= 1 && !formData.coverage_period_payroll) {
        const coverageYear = years[years.length - 1];
        const yearKey = `data_${coverageYear}`;
        const yearData = data.financial_data[yearKey];
        
        if (Array.isArray(yearData)) {
          const coveragePayroll = yearData.reduce((sum: number, month: any) => {
            return sum + (month.payroll || month.employee_wages || 0);
          }, 0);
          if (coveragePayroll > 0) {
            setFormData(prev => ({
              ...prev,
              coverage_period_payroll: Math.round(coveragePayroll).toString()
            }));
          }
        }
      }
    }
  }, [data.financial_data, data.calculated_years, data.unified_upload_data]);

  // Auto-populate officer salary from 1065/K-1 tax data if available
  useEffect(() => {
    const tax1065Files = data.tax1065Files || [];
    
    if (tax1065Files.length > 0 && formData.fixed_expenses.officer_salary === 0) {
      // Find the most recent tax file with data
      const filesWithData = tax1065Files
        .filter((f: any) => f.data && (f.data.guaranteed_payments || f.data.salaries_wages || f.data.officer_compensation))
        .sort((a: any, b: any) => (b.year || 0) - (a.year || 0));
      
      if (filesWithData.length > 0) {
        const taxData = filesWithData[0].data;
        
        // Officer compensation sources (in priority order):
        // 1. Guaranteed payments (K-1) - this IS officer comp for partnerships
        // 2. Officer compensation field (if explicitly extracted)
        // 3. Salaries & wages (partial - estimate officer portion)
        let officerComp = 0;
        let source = 'verified';
        
        if (taxData.guaranteed_payments && taxData.guaranteed_payments > 0) {
          // Guaranteed payments are officer/partner compensation
          officerComp = taxData.guaranteed_payments;
        } else if (taxData.officer_compensation && taxData.officer_compensation > 0) {
          officerComp = taxData.officer_compensation;
        } else if (taxData.salaries_wages && taxData.salaries_wages > 0) {
          // Estimate: typically 15-25% of total wages for small businesses
          officerComp = Math.round(taxData.salaries_wages * 0.20);
          source = 'estimate';
        }
        
        if (officerComp > 0) {
          // Convert annual to monthly
          const monthlyOfficerComp = Math.round(officerComp / 12);
          
          setFormData(prev => ({
            ...prev,
            fixed_expenses: {
              ...prev.fixed_expenses,
              officer_salary: monthlyOfficerComp
            },
            fixed_expense_sources: {
              ...prev.fixed_expense_sources,
              officer_salary: source as any
            }
          }));
          
          console.log(`📋 Auto-populated officer salary from 1065: $${monthlyOfficerComp}/mo (source: ${source})`);
        }
      }
    }
  }, [data.tax1065Files]);

  // Auto-populate rent from lease documents (semantic extraction)
  useEffect(() => {
    const uploadedFiles = data.uploaded_files || [];
    
    // Look for lease documents with semantic extraction data
    const leaseFiles = uploadedFiles.filter((f: any) => 
      f.doc_type === 'lease' || 
      f.doc_type === 'contract' ||
      f.semantic_extraction?.document_type === 'LEASE'
    );
    
    if (leaseFiles.length > 0 && formData.fixed_expenses.rent === 0) {
      for (const file of leaseFiles) {
        const semanticData = file.semantic_extraction?.extracted_data || file.semantic_extraction;
        
        if (semanticData) {
          // Try to get monthly rent from semantic extraction
          let monthlyRent = 0;
          
          if (semanticData.monthly_rent?.value) {
            monthlyRent = semanticData.monthly_rent.value;
          } else if (semanticData.base_rent?.value) {
            // Convert to monthly if annual
            const rent = semanticData.base_rent.value;
            const period = semanticData.rent_period?.value?.toLowerCase() || 'monthly';
            monthlyRent = period.includes('annual') || period.includes('year') 
              ? Math.round(rent / 12) 
              : rent;
          }
          
          if (monthlyRent > 0) {
            setFormData(prev => ({
              ...prev,
              fixed_expenses: {
                ...prev.fixed_expenses,
                rent: Math.round(monthlyRent)
              },
              fixed_expense_sources: {
                ...prev.fixed_expense_sources,
                rent: 'verified'
              }
            }));
            
            console.log(`📋 Auto-populated rent from lease: $${monthlyRent}/mo`);
            break; // Use first valid lease found
          }
        }
      }
    }
  }, [data.uploaded_files]);

  // Auto-detect potential extra expenses from transaction anomalies during loss period
  useEffect(() => {
    // Skip if we already have extra expenses entered
    const currentTotal = Object.values(formData.extra_expense_breakdown).reduce((sum, val) => sum + (val || 0), 0);
    if (currentTotal > 0) return;
    
    const canonicalPeriods = data.unified_upload_data?.canonical_data?.periods || [];
    
    if (canonicalPeriods.length < 6) return; // Need enough data for comparison
    
    // Try to get loss event dates from ALL possible sources (priority order)
    let lossStart: Date | null = null;
    let lossEnd: Date | null = null;
    
    // SOURCE 1: Explicit date fields (if human entered them)
    const possibleStartDates = [
      data.loss_event_start,
      data.event_coverage_start,
      data.event_start_date,
      data.event_date,
      data.coverage_start_date,
      data.loss_start_date
    ].filter(Boolean);
    
    const possibleEndDates = [
      data.loss_event_end,
      data.event_coverage_end,
      data.event_end_date,
      data.coverage_end_date,
      data.loss_end_date
    ].filter(Boolean);
    
    if (possibleStartDates.length > 0) {
      lossStart = new Date(possibleStartDates[0]);
      lossEnd = possibleEndDates.length > 0 ? new Date(possibleEndDates[0]) : new Date();
      console.log(`📋 Using explicit event dates: ${possibleStartDates[0]} to ${possibleEndDates[0] || 'present'}`);
    }
    
    // SOURCE 2: Auto-detect from cached anomaly analysis (most significant revenue drop)
    if (!lossStart) {
      const cachedAnomalies = data.cached_anomaly_analysis?.anomalies || [];
      const revenueDrops = cachedAnomalies.filter((a: any) => 
        a.anomaly_type === 'revenue_drop' && 
        a.affected_period &&
        !a.affected_period.endsWith('-01') && // Skip January (seasonal)
        Math.abs(a.deviation_percent || 0) > 40
      );
      
      if (revenueDrops.length > 0) {
        // Find the most significant non-seasonal drop
        const biggestDrop = revenueDrops.reduce((max: any, curr: any) => 
          Math.abs(curr.deviation_percent || 0) > Math.abs(max.deviation_percent || 0) ? curr : max
        );
        
        const [year, month] = biggestDrop.affected_period.split('-').map(Number);
        lossStart = new Date(year, month - 1, 1);
        // Assume loss period extends 12 months
        lossEnd = new Date(year + 1, month - 1, 1);
        console.log(`📋 Auto-detected loss event from anomaly: ${biggestDrop.affected_period} (${biggestDrop.deviation_percent?.toFixed(1)}% drop)`);
      }
    }
    
    // SOURCE 3: Scan transactions for extra expense keywords (fallback)
    if (!lossStart) {
      const extraExpenseKeywords = [
        'TEMPORARY', 'RELOCATION', 'HOTEL', 'AIRBNB', 'TEMP OFFICE', 'STORAGE', 'MOVING',
        'RENTAL', 'EQUIPMENT RENTAL', 'GENERATOR', 'EXPEDITED', 'RUSH', 'OVERNIGHT',
        'EXPRESS', 'EMERGENCY', 'URGENT', 'CONTRACTOR', 'CLEANUP', 'REMEDIATION',
        'RESTORATION', 'DEBRIS', 'DAMAGE'
      ];
      
      for (const p of canonicalPeriods) {
        const transactions = p.transactions || [];
        for (const txn of transactions) {
          const description = (txn.description || '').toUpperCase();
          if (extraExpenseKeywords.some(kw => description.includes(kw))) {
            lossStart = new Date(p.year, p.month - 1, 1);
            lossEnd = new Date(lossStart.getTime() + (365 * 24 * 60 * 60 * 1000));
            console.log(`📋 Inferred loss period from transaction keywords: ${p.year}-${p.month}`);
            break;
          }
        }
        if (lossStart) break;
      }
    }
    
    if (!lossStart) {
      console.log('📋 No loss period detected - cannot auto-detect extra expenses');
      return;
    }
    
    // Separate periods into baseline (before loss) and loss period
    const baselinePeriods: any[] = [];
    const lossPeriods: any[] = [];
    
    canonicalPeriods.forEach((p: any) => {
      const periodDate = new Date(p.year, p.month - 1, 15);
      if (periodDate < lossStart!) {
        baselinePeriods.push(p);
      } else if (periodDate <= lossEnd!) {
        lossPeriods.push(p);
      }
    });
    
    if (baselinePeriods.length < 3 || lossPeriods.length < 1) {
      console.log(`📋 Insufficient data for comparison: ${baselinePeriods.length} baseline, ${lossPeriods.length} loss periods`);
      return;
    }
    
    console.log(`📋 Analyzing extra expenses: ${baselinePeriods.length} baseline periods, ${lossPeriods.length} loss periods`);
    
    // Analyze transactions for extra expense indicators
    const extraExpenseKeywords = {
      temporary_relocation: ['TEMPORARY', 'RELOCATION', 'HOTEL', 'AIRBNB', 'TEMP OFFICE', 'STORAGE', 'MOVING', 'EVACUATION', 'BOARDING'],
      temporary_equipment: ['RENTAL', 'EQUIPMENT RENTAL', 'LEASE EQUIPMENT', 'TEMPORARY EQUIPMENT', 'GENERATOR'],
      expedited_services: ['EXPEDITED', 'RUSH', 'OVERNIGHT', 'EXPRESS', 'EMERGENCY', 'URGENT', 'CONTRACTOR'],
      marketing_recovery: ['MARKETING', 'ADVERTISING', 'PROMOTION', 'REOPENING', 'GRAND OPENING', 'COMEBACK'],
      other_extra: ['CLEANUP', 'REMEDIATION', 'RESTORATION', 'DEBRIS', 'DAMAGE', 'FIRE RESTORATION']
    };
    
    // Calculate baseline category spending (monthly average)
    const baselineCategories: Record<string, number> = {};
    baselinePeriods.forEach((p: any) => {
      (p.transactions || []).forEach((txn: any) => {
        const category = (txn.category || 'Other').toUpperCase();
        if (!baselineCategories[category]) baselineCategories[category] = 0;
        baselineCategories[category] += Math.abs(txn.amount || 0);
      });
    });
    Object.keys(baselineCategories).forEach(cat => {
      baselineCategories[cat] = baselineCategories[cat] / baselinePeriods.length;
    });
    
    // Scan loss period transactions for extra expenses
    const detectedExtras: Record<string, number> = {
      temporary_relocation: 0,
      temporary_equipment: 0,
      expedited_services: 0,
      marketing_recovery: 0,
      other_extra: 0
    };
    
    lossPeriods.forEach((p: any) => {
      (p.transactions || []).forEach((txn: any) => {
        const description = (txn.description || '').toUpperCase();
        const category = (txn.category || '').toUpperCase();
        const amount = Math.abs(txn.amount || 0);
        
        // Check against extra expense keywords
        for (const [expenseType, keywords] of Object.entries(extraExpenseKeywords)) {
          if (keywords.some(kw => description.includes(kw) || category.includes(kw))) {
            detectedExtras[expenseType] += amount;
            break; // Only count once per transaction
          }
        }
        
        // Also check for NEW categories that didn't exist in baseline
        if (!baselineCategories[category] && amount > 500) {
          // New category during loss period - likely an extra expense
          detectedExtras.other_extra += amount;
        }
        
        // Check for SPIKES in existing categories (>200% of baseline)
        if (baselineCategories[category] && amount > baselineCategories[category] * 2) {
          const excess = amount - baselineCategories[category];
          if (excess > 500) {
            detectedExtras.other_extra += excess;
          }
        }
      });
    });
    
    // Update form if we detected any extra expenses
    const totalDetected = Object.values(detectedExtras).reduce((sum, val) => sum + val, 0);
    if (totalDetected > 0) {
      setFormData(prev => ({
        ...prev,
        extra_expense_breakdown: {
          temporary_relocation: Math.round(detectedExtras.temporary_relocation),
          temporary_equipment: Math.round(detectedExtras.temporary_equipment),
          expedited_services: Math.round(detectedExtras.expedited_services),
          marketing_recovery: Math.round(detectedExtras.marketing_recovery),
          other_extra: Math.round(detectedExtras.other_extra)
        },
        extra_expense_sources: {
          temporary_relocation: detectedExtras.temporary_relocation > 0 ? 'auto_detected' : 'manual',
          temporary_equipment: detectedExtras.temporary_equipment > 0 ? 'auto_detected' : 'manual',
          expedited_services: detectedExtras.expedited_services > 0 ? 'auto_detected' : 'manual',
          marketing_recovery: detectedExtras.marketing_recovery > 0 ? 'auto_detected' : 'manual',
          other_extra: detectedExtras.other_extra > 0 ? 'auto_detected' : 'manual'
        }
      }));
      console.log(`📋 Auto-detected extra expenses from loss period transactions: $${totalDetected.toLocaleString()}`);
    }
  }, [data.unified_upload_data, data.cached_anomaly_analysis, data.loss_event_start, data.loss_event_end, data.event_coverage_start, data.event_coverage_end, data.event_start_date, data.event_end_date]);

  // Apply industry defaults from BLS templates
  const applyIndustryDefaults = useCallback(() => {
    setLoadingDefaults(true);
    const businessType = data?.business_type?.toLowerCase() || 'default';
    const template = INDUSTRY_TEMPLATES[businessType] || INDUSTRY_TEMPLATES.default;
    
    setTimeout(() => {
      setFormData(prev => ({
        ...prev,
        fixed_expenses: { ...template },
        fixed_expense_sources: {
          officer_salary: 'bls_average',
          rent: 'bls_average',
          utilities: 'bls_average',
          insurance: 'bls_average',
          other_fixed: 'bls_average'
        }
      }));
      setTemplateApplied(true);
      setLoadingDefaults(false);
    }, 500);
  }, [data?.business_type]);

  // Handle input changes
  const handleChange = (field: string, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    if (onDataUpdate) {
      onDataUpdate({ [field]: value });
    }
  };

  // Handle extra expense breakdown changes
  const handleExtraExpenseChange = (field: string, value: number) => {
    setFormData(prev => ({
      ...prev,
      extra_expense_breakdown: {
        ...prev.extra_expense_breakdown,
        [field]: value
      }
    }));
  };

  // Handle fixed expense changes
  const handleFixedExpenseChange = (field: string, value: number) => {
    setFormData(prev => ({
      ...prev,
      fixed_expenses: {
        ...prev.fixed_expenses,
        [field]: value
      },
      fixed_expense_sources: {
        ...prev.fixed_expense_sources,
        [field]: 'estimate' as const
      }
    }));
  };

  // Calculate totals
  const totalExtraExpenses = Object.values(formData.extra_expense_breakdown).reduce((sum, val) => sum + (val || 0), 0);
  const totalFixedExpenses = Object.values(formData.fixed_expenses).reduce((sum, val) => sum + (val || 0), 0);
  const fixedExpensesClaimImpact = totalFixedExpenses * 12; // 12-month projection

  // Validation checks
  const hasPayrollEntered = !!(formData.pre_incident_payroll || formData.coverage_period_payroll);
  const hasExtraExpenses = totalExtraExpenses > 0;
  const hasFixedExpenses = totalFixedExpenses > 0;

  // Get missing field warnings
  const getMissingFieldWarnings = () => {
    const warnings: string[] = [];
    if (formData.fixed_expenses.officer_salary === 0) {
      warnings.push('Officer/Owner Salary is $0 - this reduces your claim value');
    }
    if (formData.fixed_expenses.rent === 0) {
      warnings.push('Rent is $0 - verify if this is accurate');
    }
    return warnings;
  };

  // Get source badge styling - Clear labeling for BLS vs actual data
  const getSourceBadge = (source: string) => {
    switch (source) {
      case 'verified':
        return { label: 'Actual', color: 'bg-green-100 text-green-700 border-green-300', icon: '✓' };
      case 'bank_data':
        return { label: 'Bank Data', color: 'bg-green-100 text-green-700 border-green-300', icon: '🏦' };
      case 'tax_form':
        return { label: 'Tax Form', color: 'bg-blue-100 text-blue-700 border-blue-300', icon: '📄' };
      case 'estimate':
        return { label: 'Estimate', color: 'bg-blue-100 text-blue-700 border-blue-300', icon: '~' };
      case 'auto_detected':
        return { label: 'Detected', color: 'bg-purple-100 text-purple-700 border-purple-300', icon: '🔍' };
      case 'bls_average':
      case 'template':
        return { label: 'BLS Avg', color: 'bg-amber-100 text-amber-700 border-amber-300', icon: '⚡' };
      case 'manual':
        return { label: 'Manual', color: 'bg-gray-100 text-gray-500 border-gray-300', icon: '✎' };
      default:
        return { label: 'Unknown', color: 'bg-gray-100 text-gray-500 border-gray-300', icon: '?' };
    }
  };

  // Count BLS average fields for warning banner
  const blsFieldCount = Object.values(formData.fixed_expense_sources).filter(
    (source) => source === 'bls_average' || source === 'template'
  ).length;
  
  const totalExpenseFields = Object.keys(formData.fixed_expense_sources).length;
  
  // Get list of fields using BLS averages
  const blsFields = Object.entries(formData.fixed_expense_sources)
    .filter(([_, source]) => source === 'bls_average' || source === 'template')
    .map(([field, _]) => {
      const labels: Record<string, string> = {
        officer_salary: 'Officer Salary',
        rent: 'Rent',
        utilities: 'Utilities',
        insurance: 'Insurance',
        other_fixed: 'Other Fixed'
      };
      return labels[field] || field;
    });

  // Handle next
  const handleNext = () => {
    onNext({
      ...data,
      ...formData,
      extra_expenses: totalExtraExpenses,
    });
  };

  // AUTO-ADVANCE: If all required data is populated, advance automatically
  // BUT: Stop if human interacts (makes changes)
  const [hasAutoAdvanced, setHasAutoAdvanced] = useState(false);
  const [humanInteracted, setHumanInteracted] = useState(false);
  const [originalValues, setOriginalValues] = useState<any>(null);
  
  // Store original AI-populated values for audit comparison
  useEffect(() => {
    if (!originalValues && (hasPayrollEntered || hasFixedExpenses)) {
      setOriginalValues({
        pre_incident_payroll: formData.pre_incident_payroll,
        coverage_period_payroll: formData.coverage_period_payroll,
        fixed_expenses: { ...formData.fixed_expenses },
        extra_expense_breakdown: { ...formData.extra_expense_breakdown },
        timestamp: new Date().toISOString(),
        source: 'ai_automation'
      });
    }
  }, [hasPayrollEntered, hasFixedExpenses, originalValues]);
  
  // Detect human interaction - stop auto-advance
  const handleHumanChange = (field: string, value: any) => {
    if (!humanInteracted) {
      setHumanInteracted(true);
      console.log('👤 Human interaction detected - pausing automation');
    }
  };
  
  // Wrap existing change handlers to detect human interaction
  const handleChangeWithAudit = (field: string, value: string | number) => {
    handleHumanChange(field, value);
    handleChange(field, value);
  };
  
  const handleFixedExpenseChangeWithAudit = (field: string, value: number) => {
    handleHumanChange(`fixed_expenses.${field}`, value);
    handleFixedExpenseChange(field, value);
  };
  
  const handleExtraExpenseChangeWithAudit = (field: string, value: number) => {
    handleHumanChange(`extra_expenses.${field}`, value);
    handleExtraExpenseChange(field, value);
  };
  
  // Create audit record of changes
  const createAuditRecord = async () => {
    if (!humanInteracted || !originalValues) return null;
    
    const changes: any[] = [];
    
    // Compare payroll
    if (formData.pre_incident_payroll !== originalValues.pre_incident_payroll) {
      changes.push({
        field: 'pre_incident_payroll',
        original: originalValues.pre_incident_payroll,
        updated: formData.pre_incident_payroll,
        original_source: 'ai_automation'
      });
    }
    if (formData.coverage_period_payroll !== originalValues.coverage_period_payroll) {
      changes.push({
        field: 'coverage_period_payroll',
        original: originalValues.coverage_period_payroll,
        updated: formData.coverage_period_payroll,
        original_source: 'ai_automation'
      });
    }
    
    // Compare fixed expenses
    for (const key of Object.keys(formData.fixed_expenses)) {
      if (formData.fixed_expenses[key] !== originalValues.fixed_expenses?.[key]) {
        changes.push({
          field: `fixed_expenses.${key}`,
          original: originalValues.fixed_expenses?.[key],
          updated: formData.fixed_expenses[key],
          original_source: formData.fixed_expense_sources?.[key] || 'ai_automation'
        });
      }
    }
    
    // Compare extra expenses
    for (const key of Object.keys(formData.extra_expense_breakdown)) {
      if (formData.extra_expense_breakdown[key] !== originalValues.extra_expense_breakdown?.[key]) {
        changes.push({
          field: `extra_expenses.${key}`,
          original: originalValues.extra_expense_breakdown?.[key],
          updated: formData.extra_expense_breakdown[key],
          original_source: formData.extra_expense_sources?.[key] || 'ai_automation'
        });
      }
    }
    
    if (changes.length > 0) {
      const auditRecord = {
        case_id: data.id || data._id,
        step: 'step_4_payroll_inputs',
        action: 'human_override',
        changes,
        original_values: originalValues,
        updated_values: {
          pre_incident_payroll: formData.pre_incident_payroll,
          coverage_period_payroll: formData.coverage_period_payroll,
          fixed_expenses: formData.fixed_expenses,
          extra_expense_breakdown: formData.extra_expense_breakdown,
        },
        timestamp: new Date().toISOString(),
        user: 'current_user', // TODO: Get from auth context
      };
      
      console.log('📝 Audit record created:', auditRecord);
      
      // Save to backend
      try {
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/v1/audit/log`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(auditRecord)
        });
      } catch (error) {
        console.error('Failed to save audit record:', error);
      }
      
      return auditRecord;
    }
    
    return null;
  };
  
  // Handle next with audit
  const handleNextWithAudit = async () => {
    // Create audit record if human made changes
    if (humanInteracted) {
      await createAuditRecord();
    }
    
    onNext({
      ...data,
      ...formData,
      extra_expenses: totalExtraExpenses,
      human_reviewed: humanInteracted,
      auto_advanced: !humanInteracted && data.auto_advanced,
    });
  };
  
  useEffect(() => {
    if (hasAutoAdvanced) return;
    if (humanInteracted) return; // Stop auto-advance if human is interacting
    
    // Check if this was an automated flow (came from auto-advance)
    const isAutomatedFlow = data.auto_advanced === true;
    
    // Check if we have minimum required data
    const hasMinimumData = hasPayrollEntered || hasFixedExpenses;
    
    // For fully automated flow, advance once we have any populated data
    if (isAutomatedFlow && hasMinimumData) {
      console.log('🚀 AUTO-ADVANCING Step 4: Data populated, proceeding');
      console.log(`   - Payroll: ${hasPayrollEntered ? '✓' : '○'}`);
      console.log(`   - Fixed Expenses: ${hasFixedExpenses ? '✓' : '○'}`);
      console.log(`   - Extra Expenses: ${hasExtraExpenses ? '✓' : '○'}`);
      
      setHasAutoAdvanced(true);
      
      // Small delay to ensure state is saved
      setTimeout(() => {
        handleNextWithAudit();
      }, 500);
    }
  }, [hasPayrollEntered, hasFixedExpenses, hasExtraExpenses, data.auto_advanced, hasAutoAdvanced, humanInteracted]);

  // Calculate completion
  const completionPercent = 75;
  const grade = hasPayrollEntered && hasFixedExpenses ? 'B' : hasPayrollEntered ? 'C' : 'D';

  // Build sidebar sections
  const buildSections = () => [
    {
      id: 'payroll',
      label: 'Payroll',
      isActive: true,
      isComplete: hasPayrollEntered,
      onClick: () => document.getElementById('payroll-section')?.scrollIntoView({ behavior: 'smooth' })
    },
    {
      id: 'extra_expenses',
      label: 'Extra Expenses',
      isActive: false,
      isComplete: hasExtraExpenses,
      onClick: () => document.getElementById('extra-expenses-section')?.scrollIntoView({ behavior: 'smooth' })
    },
    {
      id: 'fixed_expenses',
      label: 'Fixed Expenses',
      isActive: false,
      isComplete: hasFixedExpenses,
      onClick: () => document.getElementById('fixed-expenses-section')?.scrollIntoView({ behavior: 'smooth' })
    }
  ];

  // Build QA items
  const buildQAItems = () => {
    const items: Array<{id: string; label: string; status: 'missing' | 'warning' | 'ok'}> = [];
    
    if (hasPayrollEntered) {
      items.push({ id: 'payroll', label: 'Payroll entered', status: 'ok' });
    } else {
      items.push({ id: 'payroll', label: 'Enter payroll', status: 'warning' });
    }
    
    if (hasFixedExpenses) {
      items.push({ id: 'fixed', label: 'Fixed expenses set', status: 'ok' });
    }
    
    return items;
  };

  return (
    <WizardV2Layout
      currentStep={4}
      highestCompletedStep={highestCompletedStep}
      stepTitle="Payroll & Additional Inputs"
      stepSubtitle="Enter payroll and expense information for claim calculations"
      completionPercent={completionPercent}
      calculationMode={data.calculation_mode || 'standard_bi'}
      grade={grade}
      sections={buildSections()}
      qaItems={buildQAItems()}
      nextAction={!hasPayrollEntered ? {
        label: 'Enter payroll information',
        description: 'Recommended for accurate calculations'
      } : !hasFixedExpenses ? {
        label: 'Add fixed expenses',
        description: 'Maximize your claim value'
      } : undefined}
      isSaving={isSaving}
      lastSaved={lastSaved}
      onSave={onSave}
      goToStep={goToStep}
      onBack={onBack}
      onNext={handleNextWithAudit}
      claimId={claimId}
      nextLabel="Continue to Review"
      isNextDisabled={false}
      showNext={true}
    >
      {/* All content on ONE scrollable page */}
      <div className="p-6 lg:p-8 space-y-6">
        
        {/* Payroll Information Card */}
        <div id="payroll-section" className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Wallet className="w-5 h-5 text-blue-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Payroll Information</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Pre-Incident Annual Payroll */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Pre-Incident Annual Payroll
                </label>
                {formData.payroll_sources.pre_incident_payroll !== 'manual' && (
                  <span className={`text-xs px-2 py-0.5 rounded-full border ${getSourceBadge(formData.payroll_sources.pre_incident_payroll).color}`}>
                    {getSourceBadge(formData.payroll_sources.pre_incident_payroll).icon} {getSourceBadge(formData.payroll_sources.pre_incident_payroll).label}
                    {data.calculated_years?.length >= 2 && ` (${data.calculated_years[data.calculated_years.length - 2]})`}
                  </span>
                )}
              </div>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-lg">$</span>
                <input
                  type="text"
                  value={formData.pre_incident_payroll ? Number(formData.pre_incident_payroll).toLocaleString() : ''}
                  onChange={(e) => handleChangeWithAudit('pre_incident_payroll', e.target.value.replace(/[^0-9]/g, ''))}
                  placeholder="462,000"
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg font-medium"
                />
              </div>
            </div>

            {/* Coverage Period Payroll */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Coverage Period Payroll
                </label>
                {formData.payroll_sources.coverage_period_payroll !== 'manual' && (
                  <span className={`text-xs px-2 py-0.5 rounded-full border ${getSourceBadge(formData.payroll_sources.coverage_period_payroll).color}`}>
                    {getSourceBadge(formData.payroll_sources.coverage_period_payroll).icon} {getSourceBadge(formData.payroll_sources.coverage_period_payroll).label}
                    {data.calculated_years?.length >= 1 && ` (${data.calculated_years[data.calculated_years.length - 1]})`}
                  </span>
                )}
              </div>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-lg">$</span>
                <input
                  type="text"
                  value={formData.coverage_period_payroll ? Number(formData.coverage_period_payroll).toLocaleString() : ''}
                  onChange={(e) => handleChangeWithAudit('coverage_period_payroll', e.target.value.replace(/[^0-9]/g, ''))}
                  placeholder="115,500"
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg font-medium"
                />
              </div>
            </div>
          </div>

          {/* Info Banner */}
          <div className="mt-6 bg-blue-50 rounded-lg p-4 flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-blue-800">
              <strong>Auto-calculated from your uploaded P&L data.</strong> These values sum the payroll/employee wages from your financial records. Edit if needed.
            </p>
          </div>
        </div>

        {/* Extra Expenses Card */}
        <div id="extra-expenses-section" className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <Receipt className="w-5 h-5 text-amber-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Extra Expenses</h3>
          </div>

          {/* Auto-detected notice */}
          {Object.values(formData.extra_expense_sources).some(s => s === 'auto_detected') && (
            <div className="mb-4 bg-purple-50 border border-purple-200 rounded-lg p-3 flex items-start gap-2">
              <span className="text-purple-600">🔍</span>
              <div className="text-sm text-purple-800">
                <strong>Auto-detected from transactions:</strong> We found unusual expenses during your loss period that may qualify as extra expenses. Please verify these amounts.
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Temporary Relocation */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Temporary Relocation
                </label>
                {formData.extra_expense_sources.temporary_relocation === 'auto_detected' && formData.extra_expense_breakdown.temporary_relocation > 0 && (
                  <span className="inline-flex items-center gap-1 text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full border border-purple-300">
                    🔍 Detected
                  </span>
                )}
              </div>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                <input
                  type="number"
                  value={formData.extra_expense_breakdown.temporary_relocation || ''}
                  onChange={(e) => handleExtraExpenseChangeWithAudit('temporary_relocation', parseFloat(e.target.value) || 0)}
                  placeholder="15,000"
                  className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {/* Equipment */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Equipment
                </label>
                {formData.extra_expense_sources.temporary_equipment === 'auto_detected' && formData.extra_expense_breakdown.temporary_equipment > 0 && (
                  <span className="inline-flex items-center gap-1 text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full border border-purple-300">
                    🔍 Detected
                  </span>
                )}
              </div>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                <input
                  type="number"
                  value={formData.extra_expense_breakdown.temporary_equipment || ''}
                  onChange={(e) => handleExtraExpenseChangeWithAudit('temporary_equipment', parseFloat(e.target.value) || 0)}
                  placeholder="8,000"
                  className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {/* Expedited Services */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Expedited Services
                </label>
                {formData.extra_expense_sources.expedited_services === 'auto_detected' && formData.extra_expense_breakdown.expedited_services > 0 && (
                  <span className="inline-flex items-center gap-1 text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full border border-purple-300">
                    🔍 Detected
                  </span>
                )}
              </div>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                <input
                  type="number"
                  value={formData.extra_expense_breakdown.expedited_services || ''}
                  onChange={(e) => handleExtraExpenseChangeWithAudit('expedited_services', parseFloat(e.target.value) || 0)}
                  placeholder="12,000"
                  className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {/* Marketing Recovery */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Marketing Recovery
                </label>
                {formData.extra_expense_sources.marketing_recovery === 'auto_detected' && formData.extra_expense_breakdown.marketing_recovery > 0 && (
                  <span className="inline-flex items-center gap-1 text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full border border-purple-300">
                    🔍 Detected
                  </span>
                )}
              </div>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                <input
                  type="number"
                  value={formData.extra_expense_breakdown.marketing_recovery || ''}
                  onChange={(e) => handleExtraExpenseChangeWithAudit('marketing_recovery', parseFloat(e.target.value) || 0)}
                  placeholder="10,000"
                  className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {/* Other */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Other
                </label>
                {formData.extra_expense_sources.other_extra === 'auto_detected' && formData.extra_expense_breakdown.other_extra > 0 && (
                  <span className="inline-flex items-center gap-1 text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full border border-purple-300">
                    🔍 Detected
                  </span>
                )}
              </div>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                <input
                  type="number"
                  value={formData.extra_expense_breakdown.other_extra || ''}
                  onChange={(e) => handleExtraExpenseChangeWithAudit('other_extra', parseFloat(e.target.value) || 0)}
                  placeholder="5,000"
                  className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Total Display */}
          {totalExtraExpenses > 0 && (
            <div className="mt-6 bg-amber-50 rounded-lg p-4 flex items-center justify-between">
              <span className="text-sm font-medium text-amber-800">Total Extra Expenses</span>
              <span className="text-lg font-bold text-amber-900">${totalExtraExpenses.toLocaleString()}</span>
            </div>
          )}
        </div>

        {/* Fixed Expenses Card - Collapsible */}
        <div id="fixed-expenses-section" className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border-2 border-green-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-green-900">Maximize Your Claim - Fixed Expenses</h3>
                <p className="text-sm text-green-700">Continuing expenses during closure are recoverable</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setShowFixedExpenses(!showFixedExpenses)}
              className="text-green-600 hover:text-green-800 font-medium text-sm"
            >
              {showFixedExpenses ? 'Collapse ↑' : 'Expand ↓'}
            </button>
          </div>

          {showFixedExpenses && (
            <div className="space-y-4">
              {/* BLS DATA SOURCE WARNING BANNER */}
              {blsFieldCount > 0 && (
                <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <AlertTriangle className="w-5 h-5 text-amber-600" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-amber-900 flex items-center gap-2">
                        Data Source Warning
                        <span className="text-xs bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full">
                          {blsFieldCount} of {totalExpenseFields} fields
                        </span>
                      </h4>
                      <p className="text-sm text-amber-800 mt-1">
                        Some values are using <strong>BLS Industry Averages</strong> instead of your actual figures.
                        For maximum claim defensibility, replace with documented amounts from:
                      </p>
                      <div className="flex flex-wrap gap-2 mt-2 text-xs text-amber-700">
                        <span className="flex items-center gap-1 bg-white px-2 py-1 rounded border border-amber-200">
                          <FileText className="w-3 h-3" /> Tax Returns
                        </span>
                        <span className="flex items-center gap-1 bg-white px-2 py-1 rounded border border-amber-200">
                          <FileText className="w-3 h-3" /> Bank Statements
                        </span>
                        <span className="flex items-center gap-1 bg-white px-2 py-1 rounded border border-amber-200">
                          <FileText className="w-3 h-3" /> Accounting Records
                        </span>
                      </div>
                      
                      {/* Fields using BLS averages */}
                      <div className="mt-3 pt-3 border-t border-amber-200">
                        <p className="text-xs text-amber-700 mb-2">Fields using BLS averages:</p>
                        <div className="flex flex-wrap gap-1">
                          {blsFields.map((field, i) => (
                            <span 
                              key={i}
                              className="inline-flex items-center gap-1 text-xs bg-amber-100 text-amber-800 px-2 py-1 rounded-full border border-amber-300"
                            >
                              ⚡ {field}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* All fields verified banner */}
              {blsFieldCount === 0 && hasFixedExpenses && (
                <div className="bg-green-50 border-2 border-green-300 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-green-900">All Expense Data Verified</h4>
                      <p className="text-sm text-green-700">
                        All fixed expense values are using actual figures. Your claim has maximum defensibility.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Missing field warnings */}
              {getMissingFieldWarnings().length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-amber-800">Missing key expenses:</p>
                      <ul className="text-sm text-amber-700 mt-1">
                        {getMissingFieldWarnings().map((warning, i) => (
                          <li key={i}>• {warning}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {/* Template Apply Button */}
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">
                  Business Type: <strong className="text-gray-900">{data?.business_type || 'General'}</strong>
                </span>
                <button
                  type="button"
                  onClick={applyIndustryDefaults}
                  disabled={loadingDefaults}
                  className="text-sm text-blue-600 hover:text-blue-800 font-medium underline disabled:opacity-50"
                >
                  {loadingDefaults ? 'Loading...' : '↻ Apply Industry Defaults (BLS Median)'}
                </button>
              </div>

              {templateApplied && (
                <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-100 px-3 py-2 rounded-lg border border-amber-200">
                  <Edit3 className="w-4 h-4" />
                  <span>
                    <strong>BLS Industry Averages applied.</strong> Edit each field below and mark as "Actual" for maximum claim defensibility.
                  </span>
                </div>
              )}

              {/* Fixed Expense Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Officer/Owner Salary */}
                <div className={`bg-white rounded-lg border-2 p-4 ${formData.fixed_expense_sources.officer_salary === 'verified' ? 'border-green-300' : formData.fixed_expense_sources.officer_salary === 'bls_average' || formData.fixed_expense_sources.officer_salary === 'template' ? 'border-amber-200' : 'border-gray-200'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-gray-700">Officer/Owner Salary</label>
                    <span className={`text-xs px-2 py-0.5 rounded-full border ${getSourceBadge(formData.fixed_expense_sources.officer_salary).color}`}>
                      {getSourceBadge(formData.fixed_expense_sources.officer_salary).icon} {getSourceBadge(formData.fixed_expense_sources.officer_salary).label}
                    </span>
                  </div>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                    <input
                      type="number"
                      value={formData.fixed_expenses.officer_salary || ''}
                      onChange={(e) => handleFixedExpenseChangeWithAudit('officer_salary', parseFloat(e.target.value) || 0)}
                      placeholder="0"
                      className="w-full pl-8 pr-12 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">/mo</span>
                  </div>
                  {(formData.fixed_expense_sources.officer_salary === 'bls_average' || formData.fixed_expense_sources.officer_salary === 'template') && (
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({
                        ...prev,
                        fixed_expense_sources: { ...prev.fixed_expense_sources, officer_salary: 'verified' }
                      }))}
                      className="mt-2 text-xs text-green-600 hover:text-green-800 font-medium flex items-center gap-1"
                    >
                      <CheckCircle className="w-3 h-3" /> Mark as Actual (from records)
                    </button>
                  )}
                </div>

                {/* Monthly Rent */}
                <div className={`bg-white rounded-lg border-2 p-4 ${formData.fixed_expense_sources.rent === 'verified' ? 'border-green-300' : formData.fixed_expense_sources.rent === 'bls_average' || formData.fixed_expense_sources.rent === 'template' ? 'border-amber-200' : 'border-gray-200'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-gray-700">Monthly Rent</label>
                    <span className={`text-xs px-2 py-0.5 rounded-full border ${getSourceBadge(formData.fixed_expense_sources.rent).color}`}>
                      {getSourceBadge(formData.fixed_expense_sources.rent).icon} {getSourceBadge(formData.fixed_expense_sources.rent).label}
                    </span>
                  </div>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                    <input
                      type="number"
                      value={formData.fixed_expenses.rent || ''}
                      onChange={(e) => handleFixedExpenseChangeWithAudit('rent', parseFloat(e.target.value) || 0)}
                      placeholder="0"
                      className="w-full pl-8 pr-12 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">/mo</span>
                  </div>
                  {(formData.fixed_expense_sources.rent === 'bls_average' || formData.fixed_expense_sources.rent === 'template') && (
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({
                        ...prev,
                        fixed_expense_sources: { ...prev.fixed_expense_sources, rent: 'verified' }
                      }))}
                      className="mt-2 text-xs text-green-600 hover:text-green-800 font-medium flex items-center gap-1"
                    >
                      <CheckCircle className="w-3 h-3" /> Mark as Actual (from records)
                    </button>
                  )}
                </div>

                {/* Utilities */}
                <div className={`bg-white rounded-lg border-2 p-4 ${formData.fixed_expense_sources.utilities === 'verified' ? 'border-green-300' : formData.fixed_expense_sources.utilities === 'bls_average' || formData.fixed_expense_sources.utilities === 'template' ? 'border-amber-200' : 'border-gray-200'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-gray-700">Utilities</label>
                    <span className={`text-xs px-2 py-0.5 rounded-full border ${getSourceBadge(formData.fixed_expense_sources.utilities).color}`}>
                      {getSourceBadge(formData.fixed_expense_sources.utilities).icon} {getSourceBadge(formData.fixed_expense_sources.utilities).label}
                    </span>
                  </div>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                    <input
                      type="number"
                      value={formData.fixed_expenses.utilities || ''}
                      onChange={(e) => handleFixedExpenseChangeWithAudit('utilities', parseFloat(e.target.value) || 0)}
                      placeholder="0"
                      className="w-full pl-8 pr-12 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">/mo</span>
                  </div>
                  {(formData.fixed_expense_sources.utilities === 'bls_average' || formData.fixed_expense_sources.utilities === 'template') && (
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({
                        ...prev,
                        fixed_expense_sources: { ...prev.fixed_expense_sources, utilities: 'verified' }
                      }))}
                      className="mt-2 text-xs text-green-600 hover:text-green-800 font-medium flex items-center gap-1"
                    >
                      <CheckCircle className="w-3 h-3" /> Mark as Actual (from records)
                    </button>
                  )}
                </div>

                {/* Insurance */}
                <div className={`bg-white rounded-lg border-2 p-4 ${formData.fixed_expense_sources.insurance === 'verified' ? 'border-green-300' : formData.fixed_expense_sources.insurance === 'bls_average' || formData.fixed_expense_sources.insurance === 'template' ? 'border-amber-200' : 'border-gray-200'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-gray-700">Insurance</label>
                    <span className={`text-xs px-2 py-0.5 rounded-full border ${getSourceBadge(formData.fixed_expense_sources.insurance).color}`}>
                      {getSourceBadge(formData.fixed_expense_sources.insurance).icon} {getSourceBadge(formData.fixed_expense_sources.insurance).label}
                    </span>
                  </div>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                    <input
                      type="number"
                      value={formData.fixed_expenses.insurance || ''}
                      onChange={(e) => handleFixedExpenseChangeWithAudit('insurance', parseFloat(e.target.value) || 0)}
                      placeholder="0"
                      className="w-full pl-8 pr-12 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">/mo</span>
                  </div>
                  {(formData.fixed_expense_sources.insurance === 'bls_average' || formData.fixed_expense_sources.insurance === 'template') && (
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({
                        ...prev,
                        fixed_expense_sources: { ...prev.fixed_expense_sources, insurance: 'verified' }
                      }))}
                      className="mt-2 text-xs text-green-600 hover:text-green-800 font-medium flex items-center gap-1"
                    >
                      <CheckCircle className="w-3 h-3" /> Mark as Actual (from records)
                    </button>
                  )}
                </div>

                {/* Other Fixed */}
                <div className={`bg-white rounded-lg border-2 p-4 md:col-span-2 ${formData.fixed_expense_sources.other_fixed === 'verified' ? 'border-green-300' : formData.fixed_expense_sources.other_fixed === 'bls_average' || formData.fixed_expense_sources.other_fixed === 'template' ? 'border-amber-200' : 'border-gray-200'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-gray-700">Other Fixed (loans, leases, subscriptions)</label>
                    <span className={`text-xs px-2 py-0.5 rounded-full border ${getSourceBadge(formData.fixed_expense_sources.other_fixed).color}`}>
                      {getSourceBadge(formData.fixed_expense_sources.other_fixed).icon} {getSourceBadge(formData.fixed_expense_sources.other_fixed).label}
                    </span>
                  </div>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                    <input
                      type="number"
                      value={formData.fixed_expenses.other_fixed || ''}
                      onChange={(e) => handleFixedExpenseChangeWithAudit('other_fixed', parseFloat(e.target.value) || 0)}
                      placeholder="0"
                      className="w-full pl-8 pr-12 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">/mo</span>
                  </div>
                  {(formData.fixed_expense_sources.other_fixed === 'bls_average' || formData.fixed_expense_sources.other_fixed === 'template') && (
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({
                        ...prev,
                        fixed_expense_sources: { ...prev.fixed_expense_sources, other_fixed: 'verified' }
                      }))}
                      className="mt-2 text-xs text-green-600 hover:text-green-800 font-medium flex items-center gap-1"
                    >
                      <CheckCircle className="w-3 h-3" /> Mark as Actual (from records)
                    </button>
                  )}
                </div>
              </div>

              {/* Impact Preview */}
              {totalFixedExpenses > 0 && (
                <div className="bg-gradient-to-r from-green-100 to-emerald-100 rounded-lg p-4 border border-green-300">
                  <div className="flex items-center gap-2 mb-3">
                    <Sparkles className="w-5 h-5 text-green-600" />
                    <h4 className="font-semibold text-green-900">Projected Claim Impact (12 months)</h4>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-green-600 mb-1">Monthly Fixed Expenses</p>
                      <p className="text-xl font-bold text-green-800">
                        ${totalFixedExpenses.toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-green-600 mb-1">Potential Claim Addition</p>
                      <p className="text-xl font-bold text-green-800">
                        ${fixedExpensesClaimImpact.toLocaleString()}
                      </p>
                    </div>
                  </div>
                  
                  <div className="mt-3 p-2 bg-green-200/50 rounded-lg">
                    <p className="text-sm text-green-800">
                      🎯 <strong>These fixed expenses can add ${fixedExpensesClaimImpact.toLocaleString()} to your recoverable claim</strong>
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

      </div>
    </WizardV2Layout>
  );
}
